import React, { ReactElement, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { AxiosError, AxiosResponse } from 'axios';
import styled from 'styled-components';

import { AuthApiResponse, AuthenticationResultCode } from 'features/auth';
import { Page, Box, Typography, Flex, ButtonIcon, HelperText, Loading } from 'components';
import { request, createUrl, useFormikHandles } from 'app/utils';
import { useSchool } from 'features/school';
import Alert, { useAlert } from 'features/alert';
import { AuthButton, AuthInput2 } from 'features/auth/components';

const ButtonShowPassword = styled(ButtonIcon)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 15px;
`;

const ResetPasswordView = (): ReactElement => {
  const { apiGatewayUrl, tenantUuid, schoolUuid } = useSchool();
  const [showPassword, toggleShowPassword] = useState(false);
  const { recoveryToken } = useParams();
  const { setAlert, clearAlert } = useAlert();
  const navigate = useNavigate();

  const initialValues: Record<string, any> = {
    password: '',
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      password: Yup.string().required('Você precisa informar uma senha de acesso.'),
      // .matches(
      //   /^(?=.*[A-Za-z])(?=.*\d)(?=.*[%?!@#$&*])[A-Za-z\d%?!@#$&*]{8,}$/,
      //   'Por segurança, sua senha deve conter no mínimo 8 caracteres sendo pelo menos 1 letra maiúscula, 1 letra minúscula, 1 número e um caractere especial (%?!@#$&*).'
      // ),
    }),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      const { password } = values;

      request(
        {
          url: '/api/v1/User/PasswordChange',
          method: 'post',
          baseURL: apiGatewayUrl,
          data: {
            newPassword: password,
            recoveryToken,
            tenantUuid,
          },
        },
        true
      )
        .then((response: AxiosResponse<AuthApiResponse>) => {
          setSubmitting(false);
          resetForm();

          navigate('/login');

          setTimeout(() => {
            setAlert({
              type: 'success',
              message: '<strong>Sua senha foi definida com sucesso.</strong><br />Você já pode acessar a plataforma.',
            });
          }, 50);
        })
        .catch(
          (
            error: AxiosError<{
              authenticationResult: number;
              message: string;
            }>
          ) => {
            setSubmitting(false);

            let message = 'Não foi possível redefinir sua senha.<br />Por favor, repita o processo clicando em "Esqueci a senha"';

            if (error?.response?.data?.message) {
              message = error?.response?.data?.message;
            }

            if (error?.response?.data?.authenticationResult) {
              switch (error?.response?.data?.authenticationResult) {
                case AuthenticationResultCode.InvalidRecoveryToken:
                  message =
                    "Token de recuperação inválido. Por favor, Por favor, crie uma senha nova através do link 'Esqueci a senha', localizado logo abaixo";
                  break;
                default:
                  break;
              }
            }

            setAlert({
              type: 'error',
              message,
            });
          }
        );
    },
  });

  const { formikHandleFocus, formikHandleBlur, formikHandleChange } = useFormikHandles(formik);

  useEffect(() => {
    return () => {
      clearAlert(); // Clear Alert
    };
  }, []);

  return (
    <Page title="Login">
      {formik.isSubmitting && <Loading alpha />}

      <Alert />

      <form onSubmit={formik.handleSubmit}>
        <Flex flexDirection="column">
          <Flex flexDirection="column" px={{ m: 30, d: 45 }} pt={{ m: 30, d: 45 }}>
            <Typography type="type6" color="authLabelColor" fontWeight="bold">
              Redefinir senha
            </Typography>

            <Flex flexDirection="column" mt={2}>
              <Typography lineHeight="1.2" type="type2" color="authLabelColor">
                Preencha o campo abaixo para criar uma nova senha.
              </Typography>

              {/* password */}
              <Flex flexDirection="column" mt={{ m: 30, d: 30 }}>
                <Typography type="type2" color="authLabelColor" upper fontWeight="bold">
                  Senha de acesso:
                </Typography>

                <Box position="relative" mt={2}>
                  <ButtonShowPassword
                    onClick={() => toggleShowPassword((prev) => !prev)}
                    type="button"
                    icon={showPassword ? 'eyeOff' : 'eye'}
                    tabIndex={3}
                    color="primary_contrast"
                    bg="transparent"
                    iconWidth={22}
                  />

                  <AuthInput2
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    onChange={formikHandleChange}
                    onFocus={formikHandleFocus}
                    onBlur={formikHandleBlur}
                    value={formik.values.password}
                    data-haserror={formik.touched.password && formik.errors.password}
                    tabIndex={2}
                    style={{ paddingRight: 50 }}
                  />
                </Box>

                {formik.touched.password && formik.errors.password ? (
                  <HelperText type="error">{formik.errors.password}</HelperText>
                ) : schoolUuid === '76cebc79-bf6c-4144-bab2-947362f3f8c4' ? (
                  <Typography type="type0" color="gray_500" lineHeight={1.4} mt={3}>
                    Por segurança, sua senha deve conter no mínimo 6 caracteres.
                  </Typography>
                ) : (
                  <Typography type="type0" color="gray_500" lineHeight={1.4} mt={3}>
                    Por segurança, sua senha deve conter no mínimo 8 caracteres sendo pelo menos 1 letra maiúscula, 1 letra minúscula, 1 número e um
                    caractere especial (%?!@#$&*).
                  </Typography>
                )}
              </Flex>
              {/* /password */}

              {/* <Flex justifyContent="space-between" mt={3}>
                <Link tabIndex={4} to={createUrl('login')}>
                  <Typography type="type1" color="authLabelColor" colorHover="primary_contrast" upper>
                    Voltar para o login
                  </Typography>
                </Link>

                <Link tabIndex={4} to={createUrl('forgotPassword')}>
                  <Typography type="type1" color="authLabelColor" colorHover="primary_contrast" upper>
                    Esqueci a senha
                  </Typography>
                </Link>
              </Flex> */}
            </Flex>
          </Flex>

          <Flex mt={5}>
            <AuthButton type="submit">Enviar</AuthButton>
          </Flex>

          <Flex bg="authBackground" justifyContent="center" py={3}>
            <Link to={createUrl('login')}>
              <Typography className="btn-auth-external" color="authFloatLinkColor">
                Voltar para o login
              </Typography>
            </Link>
          </Flex>
        </Flex>
      </form>
    </Page>
  );
};

export default ResetPasswordView;
